import api from '@/shared/api';
import { extractApiErrorCode, extractApiErrorMessage } from '@/shared/lib';
import type { AuditUserResponse } from '@/shared/lib';
import type { SendMatrixToApprovalDto } from '../lib/approval-submission';
import type { FormDefinition } from '../lib/form-serializer';
import type { FormRulesStructure } from '../model/form-rules.store';

export interface MatrixCategory {
  id: number;
  name: string;
}

export interface Matrix {
  id: number;
  prefix: string;
  title: string;
  matrixStructure: FormDefinition;
  rulesStructure?: object | null;
  permissionsStructure?: object | null;
  isDraft: boolean;
  /** null while the row is a draft; set once it is sent for approval. */
  revision?: number | null;
  status?: string | null;
  approvalManagerId?: number | null;
  validityDate?: string | null;
  effectiveDate?: string | null;
  generatedRecordsCount?: number;
  createdAt: string;
  updatedAt: string;
  createdBy?: number | null;
  updatedBy?: number | null;
  creator?: AuditUserResponse | null;
  updater?: AuditUserResponse | null;
}

export interface MatrixModel {
  id: number;
  title: string;
  structure: FormDefinition;
  branchId: number;
  createdAt: string;
  updatedAt: string;
  createdBy?: number | null;
  updatedBy?: number | null;
  creator?: AuditUserResponse | null;
  updater?: AuditUserResponse | null;
  categories: MatrixCategory[];
}

export interface MatrixModelSelectionItem {
  id: number;
  title: string;
  createdAt: string;
  categories: MatrixCategory[];
  structure: FormDefinition;
}

export interface CreateMatrixDto {
  prefix: string;
  title: string;
  matrixStructure: FormDefinition;
  rulesStructure?: object | null;
  permissionsStructure?: object | null;
  isDraft?: boolean;
}

export interface UpdateMatrixDto {
  prefix?: string;
  title?: string;
  matrixStructure: FormDefinition;
  rulesStructure?: object | null;
  permissionsStructure?: object | null;
}

export interface CreateMatrixModelDto {
  title: string;
  structure: FormDefinition;
  categoryIds: number[];
}

export interface UpdateMatrixModelDto {
  title?: string;
  structure?: FormDefinition;
  categoryIds?: number[];
}

export interface ListMatrixModelsParams {
  search?: string;
  sort?: 'createdAt' | 'title';
  order?: 'asc' | 'desc';
  createdAt?: string;
  categoryIds?: number[];
}

export interface ListMatricesResponse {
  matrices: Matrix[];
  total: number;
}

/**
 * Form of a Registration that sits in a homologation queue. It is the only payload that
 * carries `matrixStructure`, which is around 136 KB for a seeded Registration, so the queue
 * detail keeps it out and this shape is fetched only by the screens that draw the form.
 * `rulesStructure` comes as `null`, never missing, when the Registration has no rule.
 */
export interface MatrixQueueStructure {
  id: number;
  /** Already labelled, `RQ4.2`: print it as it arrives. */
  prefix: string;
  title: string;
  matrixStructure: FormDefinition;
  rulesStructure: FormRulesStructure | null;
}

export interface SendMatrixToApprovalResponse {
  /** Carries revision/status/approvalManagerId: there is no separate revision row. */
  matrix: Matrix;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  message?: string;
}

export class FormBuilderApi {
  async saveMatrix(data: CreateMatrixDto): Promise<Matrix> {
    const response = await api.post<ApiEnvelope<Matrix>>('/matrices', data);
    return response.data.data;
  }

  async sendToApproval(data: SendMatrixToApprovalDto): Promise<SendMatrixToApprovalResponse> {
    const response = await api.post<ApiEnvelope<SendMatrixToApprovalResponse>>(
      '/matrices/send-to-approval',
      data
    );
    return response.data.data;
  }

  async loadMatrix(id: number): Promise<Matrix> {
    const response = await api.get<ApiEnvelope<Matrix>>(`/matrices/${id}`);
    return response.data.data;
  }

  async listMatrices(page = 1, limit = 50): Promise<ListMatricesResponse> {
    const response = await api.get<ApiEnvelope<Matrix[]>>('/matrices', {
      params: { page, limit },
    });
    return {
      matrices: response.data.data,
      total: response.data.data.length,
    };
  }

  async updateMatrix(id: number, data: UpdateMatrixDto): Promise<Matrix> {
    const response = await api.put<ApiEnvelope<Matrix>>(`/matrices/${id}`, data);
    return response.data.data;
  }

  async deleteMatrix(id: number): Promise<void> {
    await api.delete(`/matrices/${id}`);
  }
}

export const matrixBuilderApi = new FormBuilderApi();

export const matrixCategoryApi = {
  async list(): Promise<MatrixCategory[]> {
    const response = await api.get<ApiEnvelope<MatrixCategory[]>>('/matrix-categories');
    return response.data.data;
  },
  async create(name: string): Promise<MatrixCategory> {
    const response = await api.post<ApiEnvelope<MatrixCategory>>('/matrix-categories', { name });
    return response.data.data;
  },
};

const CATEGORY_NAME_CONFLICT_CODE = 'MATRIX_CATEGORY_NAME_ALREADY_EXISTS';

export function isCategoryNameConflictError(error: unknown): boolean {
  return extractApiErrorCode(error) === CATEGORY_NAME_CONFLICT_CODE;
}

export const matrixModelApi = {
  async create(data: CreateMatrixModelDto): Promise<MatrixModel> {
    const response = await api.post<ApiEnvelope<MatrixModel>>('/matrix-models', data);
    return response.data.data;
  },
  async list(params: ListMatrixModelsParams = {}): Promise<MatrixModelSelectionItem[]> {
    const response = await api.get<ApiEnvelope<MatrixModelSelectionItem[]>>(
      '/matrix-models/selection',
      {
        params: {
          ...params,
          categoryIds: params.categoryIds?.length ? params.categoryIds.join(',') : undefined,
        },
      }
    );
    return response.data.data;
  },
  async getById(id: number): Promise<MatrixModel> {
    const response = await api.get<ApiEnvelope<MatrixModel>>(`/matrix-models/${id}`);
    return response.data.data;
  },
  async update(id: number, data: UpdateMatrixModelDto): Promise<MatrixModel> {
    const response = await api.put<ApiEnvelope<MatrixModel>>(`/matrix-models/${id}`, data);
    return response.data.data;
  },
  async delete(id: number): Promise<void> {
    await api.delete(`/matrix-models/${id}`);
  },
};

export const MODEL_TITLE_CONFLICT_MESSAGE = 'Já existe um modelo com esse nome nesta categoria.';

const MODEL_TITLE_CONFLICT_CODE = 'MATRIX_MODEL_TITLE_ALREADY_EXISTS';

/**
 * The conflict travels as a 400 with an errorCode, because this API has no 409. The message is the
 * fallback for a server that answers before the code existed.
 */
export function isModelTitleConflictError(error: unknown): boolean {
  return (
    extractApiErrorCode(error) === MODEL_TITLE_CONFLICT_CODE ||
    extractApiErrorMessage(error) === MODEL_TITLE_CONFLICT_MESSAGE
  );
}

export const MATRIX_RECORD_FIELDS_NOT_EDITABLE_MESSAGE =
  'Não é possível atualizar o RQ do Registro.';

const MATRIX_RECORD_FIELDS_NOT_EDITABLE_CODE = 'MATRIX_RECORD_FIELDS_NOT_EDITABLE';

export function isMatrixRecordFieldsNotEditableError(error: unknown): boolean {
  return (
    extractApiErrorCode(error) === MATRIX_RECORD_FIELDS_NOT_EDITABLE_CODE ||
    extractApiErrorMessage(error) === MATRIX_RECORD_FIELDS_NOT_EDITABLE_MESSAGE
  );
}

export const MATRIX_STRUCTURE_INDEXING_LOCKED_MESSAGE =
  'Esta revisão já gerou Registro. Os campos do título e do indexador não podem mudar; crie uma revisão nova para mudar.';

const MATRIX_STRUCTURE_INDEXING_LOCKED_CODE = 'MATRIX_STRUCTURE_INDEXING_LOCKED';

export function isMatrixStructureIndexingLockedError(error: unknown): boolean {
  return (
    extractApiErrorCode(error) === MATRIX_STRUCTURE_INDEXING_LOCKED_CODE ||
    extractApiErrorMessage(error) === MATRIX_STRUCTURE_INDEXING_LOCKED_MESSAGE
  );
}

export const MATRIX_HAS_GENERATED_RECORDS_MESSAGE =
  'Esta revisão já gerou Registro e não aceita mais alterações. Para mudar a estrutura, as regras ou as permissões, crie uma nova revisão.';

const MATRIX_HAS_GENERATED_RECORDS_CODE = 'MATRIX_HAS_GENERATED_RECORDS';

export function isMatrixHasGeneratedRecordsError(error: unknown): boolean {
  return (
    extractApiErrorCode(error) === MATRIX_HAS_GENERATED_RECORDS_CODE ||
    extractApiErrorMessage(error) === MATRIX_HAS_GENERATED_RECORDS_MESSAGE
  );
}

export const MATRIX_PREFIX_NOT_EDITABLE_MESSAGE =
  'O RQ de um Registro homologado não pode ser alterado.';

const MATRIX_PREFIX_NOT_EDITABLE_CODE = 'MATRIX_PREFIX_NOT_EDITABLE';

export function isMatrixPrefixNotEditableError(error: unknown): boolean {
  return (
    extractApiErrorCode(error) === MATRIX_PREFIX_NOT_EDITABLE_CODE ||
    extractApiErrorMessage(error) === MATRIX_PREFIX_NOT_EDITABLE_MESSAGE
  );
}

const DRAFT_PREFIX_TAKEN_MESSAGE_START = 'Já existe um rascunho com o prefixo';

export function extractDraftPrefixTakenMessage(error: unknown): string | undefined {
  const message = extractApiErrorMessage(error);
  return message?.startsWith(DRAFT_PREFIX_TAKEN_MESSAGE_START) ? message : undefined;
}

export const matrixQueueStructureApi = {
  async getAwaitingApproval(matrixId: number): Promise<MatrixQueueStructure> {
    const response = await api.get<ApiEnvelope<MatrixQueueStructure>>(
      `/matrices/awaiting-approval/${matrixId}/structure`
    );
    return response.data.data;
  },
  async getAwaitingVerification(matrixId: number): Promise<MatrixQueueStructure> {
    const response = await api.get<ApiEnvelope<MatrixQueueStructure>>(
      `/matrices/awaiting-verification/${matrixId}/structure`
    );
    return response.data.data;
  },
  async getDirectoryMatrix(matrixId: number): Promise<MatrixQueueStructure> {
    const response = await api.get<ApiEnvelope<MatrixQueueStructure>>(
      `/directories/matrices/${matrixId}`
    );
    return response.data.data;
  },
};
